import React, { FC, useState } from 'react';
import { DishTask, Restaurant } from '../types';
import { 
  ClipboardList, 
  Plus, 
  Trash2, 
  User, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Camera, 
  Sparkles, 
  Layers, 
  UploadCloud, 
  Search, 
  SlidersHorizontal,
  FileText,
  UserCheck,
  TrendingUp,
  LayoutGrid,
  Table as TableIcon,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface TasksViewProps {
  tasks: DishTask[];
  restaurants: Restaurant[];
  onAddTask: (taskData: { dishName: string; restaurantId: string; executor: string; notes?: string }) => Promise<void>;
  onUpdateTask: (id: string, updatedFields: Partial<DishTask>) => Promise<void>;
  onDeleteTask: (id: string) => Promise<void>;
}

export const TasksView: FC<TasksViewProps> = ({
  tasks,
  restaurants,
  onAddTask,
  onUpdateTask,
  onDeleteTask
}) => {
  // View mode state: 'table' (default) or 'grid'
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [selectedExecutor, setSelectedExecutor] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Form State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [newDishName, setNewDishName] = useState('');
  const [newRestaurantId, setNewRestaurantId] = useState('');
  const [newExecutor, setNewExecutor] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Quick State Editing State for note tooltips
  const [activeTaskDetailId, setActiveTaskDetailId] = useState<string | null>(null);

  // Selected task state for full-featured details modal
  const [selectedTaskForModal, setSelectedTaskForModal] = useState<DishTask | null>(null);
  
  // Dynamic active task binder to keep modal in sync with inline edits
  const activeModalTask = tasks.find(t => t.id === selectedTaskForModal?.id) || null;

  // Azerbaijani names for production crew
  const uniqueExecutors: string[] = Array.from(new Set([
    ...tasks.flatMap(t => [t.executor, t.photoExecutor, t.burstExecutor, t.aiVideoExecutor, t.uploadExecutor]),
    'Anar Əliyev',
    'Rəşad Məmmədov',
    'Aysel Rzayeva',
    'Leyla Həsənova',
    'Fərid Bağırov',
    'Günel İsmayılova'
  ])).filter(Boolean) as string[];


  // Photo upload handler
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      const data = await res.json();
      setNewPhotoUrl(data.url);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  // Form submission handler
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDishName || !newRestaurantId || !newExecutor) return;

    try {
      setIsSubmitting(true);
      await onAddTask({
        dishName: newDishName,
        restaurantId: newRestaurantId,
        executor: newExecutor,
        notes: newNotes,
        photo_url: newPhotoUrl || undefined,
      });
      // Reset form variables
      setNewDishName('');
      setNewRestaurantId('');
      setNewExecutor('');
      setNewNotes('');
      setNewPhotoUrl('');
      setIsFormOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quick toggle helper for 4 types of work
  const toggleSubStatus = async (
    taskId: string, 
    field: 'photoStatus' | 'burstStatus' | 'aiVideoStatus' | 'uploadStatus', 
    currentValue: 'pending' | 'in_progress' | 'completed'
  ) => {
    let newValue: 'pending' | 'in_progress' | 'completed' = 'pending';
    if (currentValue === 'pending') {
      newValue = 'in_progress';
    } else if (currentValue === 'in_progress') {
      newValue = 'completed';
    } else {
      newValue = 'pending';
    }

    await onUpdateTask(taskId, { [field]: newValue });
  };

  // Filtering Logic
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.dishName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          task.executor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          task.restaurantName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRestaurant = selectedRestaurant === 'all' || task.restaurantId === selectedRestaurant;
    const matchesExecutor = selectedExecutor === 'all' || task.executor === selectedExecutor;
    const matchesStatus = selectedStatus === 'all' || task.status === selectedStatus;

    return matchesSearch && matchesRestaurant && matchesExecutor && matchesStatus;
  });

  // Calculate Executor performance analytics
  const executorStats = uniqueExecutors.map((exec: string) => {
    const execTasks = tasks.filter(t => t.executor === exec);
    const total = execTasks.length;
    const completed = execTasks.filter(t => t.status === 'completed').length;
    const inProgress = execTasks.filter(t => t.status === 'in_progress').length;
    const pending = execTasks.filter(t => t.status === 'pending').length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      name: exec,
      total,
      completed,
      inProgress,
      pending,
      completionRate
    };
  }).sort((a, b) => b.completed - a.completed);

  // Aggregate Metrics for Pipeline
  const totalCount = tasks.length;
  const photoDoneCount = tasks.filter(t => t.photoStatus === 'completed').length;
  const burstDoneCount = tasks.filter(t => t.burstStatus === 'completed').length;
  const aiVideoDoneCount = tasks.filter(t => t.aiVideoStatus === 'completed').length;
  const uploadDoneCount = tasks.filter(t => t.uploadStatus === 'completed').length;

  const getStatusBadge = (status: 'pending' | 'in_progress' | 'completed') => {
    switch (status) {
      case 'completed':
        return {
          bg: 'bg-emerald-50 border-emerald-150 text-emerald-750',
          dot: 'bg-emerald-500',
          label: 'Rəqəmsallaşdırılıb'
        };
      case 'in_progress':
        return {
          bg: 'bg-amber-50 border-amber-150 text-amber-700',
          dot: 'bg-amber-550',
          label: 'Hazırlanır'
        };
      default:
        return {
          bg: 'bg-slate-100 border-slate-200 text-slate-600',
          dot: 'bg-slate-400',
          label: 'Növbədə'
        };
    }
  };

  const getSubStageControl = (
    taskId: string,
    field: 'photoStatus' | 'burstStatus' | 'aiVideoStatus' | 'uploadStatus',
    status: 'pending' | 'in_progress' | 'completed',
    icon: React.ReactNode,
    title: string
  ) => {
    let buttonStyle = '';
    let statusText = '';

    switch (status) {
      case 'completed':
        buttonStyle = 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100/70';
        statusText = 'Hazırdır';
        break;
      case 'in_progress':
        buttonStyle = 'bg-amber-50 text-amber-700 border border-amber-200 animate-pulse hover:bg-amber-100/70';
        statusText = 'İcrada';
        break;
      default:
        buttonStyle = 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100';
        statusText = 'Gözlənilir';
        break;
    }

    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggleSubStatus(taskId, field, status);
        }}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all select-none ${buttonStyle}`}
        title={`${title}: ${statusText}. Klikləməklə statusu dəyişdirin.`}
      >
        {icon}
        <span className="hidden sm:inline text-[10px] uppercase font-mono tracking-wider font-bold">
          {statusText}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-8">
      {/* 1. Header with Title and Create CTA */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-extrabold text-slate-900 tracking-tight">
            Rəqəmsallaşdırma və Prodakşn Hesabatı
          </h2>
          <p className="text-slate-500 text-xs mt-1">
            Media kontentin (3D və süni intellekt videoları) hazırlanmasının və rəqəmsal QR menyu kataloqunun doldurulmasının peşəkar tənzimlənməsi
          </p>
        </div>
        
        <div className="flex items-center gap-2.5 self-start md:self-auto select-none">
          {/* Custom View Toggler */}
          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${viewMode === 'table' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-505 hover:text-slate-850'}`}
            >
              <TableIcon className="w-3.5 h-3.5" />
              <span>Cədvəl</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-505 hover:text-slate-850'}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>Kartlar</span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsFormOpen(!isFormOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
            id="btn-add-task-trigger"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni yemək</span>
          </button>
        </div>
      </div>

      {/* 3. Collapse Task Form */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <form onSubmit={handleCreateTask} className="bg-white border border-indigo-100 rounded-2xl p-6 shadow-md space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                <ClipboardList className="w-4 h-4 text-indigo-600" />
                <h3 className="text-sm font-bold text-slate-800">Rəqəmsallaşdırma konveyeri üçün yeni yemək tapşırığı</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Dish Name */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500">Yeməyin adı</label>
                  <input
                    type="text"
                    required
                    placeholder="Məsələn: Tuna balıqlı Nisuaz salatı"
                    value={newDishName}
                    onChange={(e) => setNewDishName(e.target.value)}
                    className="w-full px-3.5 py-2 border border-slate-200 focus:border-indigo-500 rounded-xl text-xs placeholder-slate-400 outline-hidden transition-all text-slate-800"
                  />
                </div>

                {/* Restaurant */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500">Sifarişçi restoran</label>
                  <select
                    required
                    value={newRestaurantId}
                    onChange={(e) => setNewRestaurantId(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 focus:border-indigo-500 rounded-xl text-xs bg-white outline-hidden transition-all text-slate-700 font-medium"
                  >
                    <option value="">-- Restoranı seçin --</option>
                    {restaurants.map(r => (
                      <option key={r.id} value={r.id}>{r.name} ({r.cuisine})</option>
                    ))}
                  </select>
                </div>

                {/* Executor/Assignee */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500">Məsul icraçı</label>
                  <input
                    type="text"
                    required
                    placeholder="Leyla Həsənova, Fərid Bağırov..."
                    value={newExecutor}
                    onChange={(e) => setNewExecutor(e.target.value)}
                    className="w-full px-3.5 py-2 border border-slate-200 focus:border-indigo-500 rounded-xl text-xs placeholder-slate-400 outline-hidden transition-all text-slate-800"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-slate-500">Texniki Tapşırıq / Süni İntellekt üçün Brif (Könüllü)</label>
                <textarea
                  rows={2}
                  placeholder="Çəkiliş xüsusiyyətləri: inqrediyentlərin yaxından çəkilişi, süni intellekt vasitəsilə buxar effekti, yeməyin kəsilməsi anı..."
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="w-full px-3.5 py-2 border border-slate-200 focus:border-indigo-500 rounded-xl text-xs placeholder-slate-400 outline-hidden transition-all text-slate-800"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 font-bold text-xs rounded-xl transition-all cursor-pointer"
                >
                  İmtina
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer"
                >
                  {isSubmitting ? 'Yadda saxlanılır...' : 'Tapşırığı təyin et'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 4. Filter Toolbar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col gap-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Yemək, icraçı və ya restoran üzrə axtarış..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50/50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl text-xs outline-hidden transition-all text-slate-800"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-slate-550 text-xs font-semibold shrink-0 select-none">
              <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400" />
              <span>Filtrlər:</span>
            </div>

            {/* Restaurant Filter */}
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 bg-white rounded-xl text-xs outline-hidden text-slate-705 font-bold shrink-0 cursor-pointer"
            >
              <option value="all">Bütün restoranlar ({restaurants.length})</option>
              {restaurants.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>

            {/* Executor Filter */}
            <select
              value={selectedExecutor}
              onChange={(e) => setSelectedExecutor(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 bg-white rounded-xl text-xs outline-hidden text-slate-705 font-bold shrink-0 cursor-pointer"
            >
              <option value="all">Bütün icraçılar ({uniqueExecutors.length})</option>
              {uniqueExecutors.map(exec => (
                <option key={exec} value={exec}>{exec}</option>
              ))}
            </select>

            {/* Global Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 bg-white rounded-xl text-xs outline-hidden text-slate-705 font-bold shrink-0 cursor-pointer"
            >
              <option value="all">Bütün statuslar</option>
              <option value="pending">Növbədə</option>
              <option value="in_progress">İcrada</option>
              <option value="completed">Həll edilib</option>
            </select>

            {/* Reset Filter Button */}
            {(searchTerm || selectedRestaurant !== 'all' || selectedExecutor !== 'all' || selectedStatus !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRestaurant('all');
                  setSelectedExecutor('all');
                  setSelectedStatus('all');
                }}
                className="px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 bg-white border border-rose-200 rounded-xl transition-all font-bold font-mono cursor-pointer"
              >
                Sıfırla
              </button>
            )}
          </div>
        </div>

        {/* Global pipeline progress meter */}
        <div className="border-t border-slate-100 pt-3 flex flex-wrap gap-x-6 gap-y-2 items-center text-xs text-slate-500 font-semibold select-none">
          <span className="text-[10px] text-slate-400 font-mono font-bold uppercase tracking-wider">Qlobal mərhələlər:</span>
          
          <div className="flex items-center gap-1.5 font-mono">
            <Camera className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
            <span>9:16 Çəkiliş: </span>
            <span className="font-bold text-slate-800">{photoDoneCount} / {totalCount}</span>
          </div>

          <div className="flex items-center gap-1.5 font-mono">
            <Layers className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
            <span>Təbəqələr (Burst): </span>
            <span className="font-bold text-slate-800">{burstDoneCount} / {totalCount}</span>
          </div>

          <div className="flex items-center gap-1.5 font-mono">
            <Sparkles className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
            <span>Sİ Video effekti: </span>
            <span className="font-bold text-slate-800">{aiVideoDoneCount} / {totalCount}</span>
          </div>

          <div className="flex items-center gap-1.5 font-mono">
            <UploadCloud className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
            <span>Menyuya yüklə: </span>
            <span className="font-bold text-slate-800">{uploadDoneCount} / {totalCount}</span>
          </div>
        </div>
      </div>

      {/* 5. Production Content (Table View by Default OR Grid View) */}
      {filteredTasks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center max-w-lg mx-auto space-y-3 shadow-xs">
          <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mx-auto text-slate-400 border border-slate-100">
            <ClipboardList className="w-5 h-5" />
          </div>
          <h3 className="font-bold text-slate-800 text-sm">Uyğun gələn tapşırıq tapılmadı</h3>
          <p className="text-xs text-slate-450 leading-relaxed">
            Rəqəmsallaşdırma konveyerinin digər tapşırıqlarını görmək üçün süzgəcləri təmizləyin və ya axtarış sorğusunu dəyişin.
          </p>
        </div>
      ) : viewMode === 'table' ? (
        /* KONTUR TABLE - THE HERO VIEW */
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-200 text-[10px] text-slate-400 font-bold uppercase font-mono tracking-wider select-none">
                  <th className="py-4 px-5">Yemək</th>
                  <th className="py-4 px-4">Restoran</th>
                  <th className="py-4 px-4">Kateqoriya</th>
                  <th className="py-4 px-4">İcraçı</th>
                  <th className="py-4 px-4 font-bold text-center">Ümumi Status</th>
                  <th className="py-4 px-3 text-center">1. 9:16 Çəkiliş</th>
                  <th className="py-4 px-3 text-center">2. Təbəqələr (Burst)</th>
                  <th className="py-4 px-3 text-center">3. Sİ Video effekti</th>
                  <th className="py-4 px-3 text-center">4. Menyuya yüklə</th>
                  <th className="py-4 px-4 text-center">Tarix</th>
                  <th className="py-4 px-5 text-right">Seçimlər</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-150 text-xs text-slate-600">
                {filteredTasks
                  .slice((currentPage - 1) * perPage, currentPage * perPage)
                  .map((task) => {
                  const bColor = getStatusBadge(task.status);
                  const isDetailOpen = activeTaskDetailId === task.id;
                  const restObj = restaurants.find(r => r.id === task.restaurantId);
                  const cuisineCategory = restObj?.cuisine || 'Mətbəx';

                  return (
                    <React.Fragment key={task.id}>
                      <tr 
                        onClick={() => setSelectedTaskForModal(task)}
                        className="hover:bg-slate-50/80 transition-colors group align-middle cursor-pointer"
                        title="Ətraflı məlumat, TT və əlaqə kartını görmək üçün klikləyin"
                      >
                        <td className="py-3 px-5">
                          <span className="font-extrabold text-slate-900 text-xs hover:text-indigo-600 transition-all block">
                            {task.dishName}
                          </span>
                        </td>

                        <td className="py-3 px-4">
                          <span className="font-bold text-slate-700 text-xs block">
                            {task.restaurantName}
                          </span>
                        </td>

                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 uppercase tracking-wide font-mono select-none">
                            {cuisineCategory}
                          </span>
                        </td>

                        <td className="py-3 px-4">
                          <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-slate-50/50 border border-slate-200 rounded-full">
                            <div className="w-5 h-5 rounded-full bg-indigo-550 text-white flex items-center justify-center font-bold text-[9px] select-none">
                              {task.executor.split(' ').map(n => n[0]).join('')}
                            </div>
                            <span className="text-xs font-bold text-slate-700 whitespace-nowrap">
                              {task.executor}
                            </span>
                          </div>
                        </td>

                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${bColor.bg} font-mono select-none`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${bColor.dot}`} />
                            <span>{bColor.label}</span>
                          </span>
                        </td>

                        {/* Workflow stages columns: Status controls + Executor selectors */}
                        <td className="py-3 px-3 col-stage">
                          <div className="flex flex-col items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {getSubStageControl(
                              task.id,
                              'photoStatus',
                              task.photoStatus,
                              <Camera className="w-3.5 h-3.5" />,
                              '9:16 Çəkiliş'
                            )}
                            <select
                              value={task.photoExecutor || task.executor}
                              onChange={async (e) => {
                                await onUpdateTask(task.id, { photoExecutor: e.target.value });
                              }}
                              className="text-[9px] font-sans font-bold text-slate-500 bg-slate-50/70 hover:bg-slate-100 border border-slate-200 rounded px-1 py-0.5 outline-hidden cursor-pointer w-[95px] text-center"
                            >
                              {uniqueExecutors.map(exec => (
                                <option key={exec} value={exec}>{exec}</option>
                              ))}
                            </select>
                          </div>
                        </td>

                        <td className="py-3 px-3 col-stage">
                          <div className="flex flex-col items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {getSubStageControl(
                              task.id,
                              'burstStatus',
                              task.burstStatus,
                              <Layers className="w-3.5 h-3.5" />,
                              'Təbəqələr (Burst)'
                            )}
                            <select
                              value={task.burstExecutor || task.executor}
                              onChange={async (e) => {
                                await onUpdateTask(task.id, { burstExecutor: e.target.value });
                              }}
                              className="text-[9px] font-sans font-bold text-slate-500 bg-slate-50/70 hover:bg-slate-100 border border-slate-200 rounded px-1 py-0.5 outline-hidden cursor-pointer w-[95px] text-center"
                            >
                              {uniqueExecutors.map(exec => (
                                <option key={exec} value={exec}>{exec}</option>
                              ))}
                            </select>
                          </div>
                        </td>

                        <td className="py-3 px-3 col-stage">
                          <div className="flex flex-col items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {getSubStageControl(
                              task.id,
                              'aiVideoStatus',
                              task.aiVideoStatus,
                              <Sparkles className="w-3.5 h-3.5" />,
                              'Sİ Video'
                            )}
                            <select
                              value={task.aiVideoExecutor || task.executor}
                              onChange={async (e) => {
                                await onUpdateTask(task.id, { aiVideoExecutor: e.target.value });
                              }}
                              className="text-[9px] font-sans font-bold text-slate-500 bg-slate-50/70 hover:bg-slate-100 border border-slate-200 rounded px-1 py-0.5 outline-hidden cursor-pointer w-[95px] text-center"
                            >
                              {uniqueExecutors.map(exec => (
                                <option key={exec} value={exec}>{exec}</option>
                              ))}
                            </select>
                          </div>
                        </td>

                        <td className="py-3 px-3 col-stage">
                          <div className="flex flex-col items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {getSubStageControl(
                              task.id,
                              'uploadStatus',
                              task.uploadStatus,
                              <UploadCloud className="w-3.5 h-3.5" />,
                              'Menyuya yüklə'
                            )}
                            <select
                              value={task.uploadExecutor || task.executor}
                              onChange={async (e) => {
                                await onUpdateTask(task.id, { uploadExecutor: e.target.value });
                              }}
                              className="text-[9px] font-sans font-bold text-slate-500 bg-slate-50/70 hover:bg-slate-100 border border-slate-200 rounded px-1 py-0.5 outline-hidden cursor-pointer w-[95px] text-center"
                            >
                              {uniqueExecutors.map(exec => (
                                <option key={exec} value={exec}>{exec}</option>
                              ))}
                            </select>
                          </div>
                        </td>

                        <td className="py-3 px-4 text-center whitespace-nowrap text-xs text-slate-400 font-mono font-bold">
                          {task.createdAt}
                        </td>

                        <td className="py-3 px-5 text-right">
                          <div className="flex items-center justify-end gap-2.5" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveTaskDetailId(isDetailOpen ? null : task.id);
                              }}
                              className={`p-1.5 rounded-lg border transition-all cursor-pointer ${isDetailOpen ? 'bg-indigo-50 border-indigo-200 text-indigo-650' : 'bg-white border-slate-200 text-slate-450 hover:text-slate-700 hover:border-slate-350'}`}
                              title="Detallı TT-ni və alt-mərhələləri gör"
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                if (confirm('Bu yeməyi istehsalat konveyerindən tamamilə silmək istədiyinizə əminsiniz?')) {
                                  onDeleteTask(task.id);
                                }
                              }}
                              className="p-1.5 hover:bg-rose-50 border border-slate-200 hover:border-rose-300 text-slate-400 hover:text-rose-600 rounded-lg transition-all cursor-pointer"
                              title="Konveyerdən sil"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Enriched Nested Sub-tasks & Specs View */}
                      {isDetailOpen && (
                        <tr className="bg-slate-50/40">
                          <td colSpan={11} className="py-4 px-6 border-b border-slate-200">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start text-left">
                              
                              <div className="lg:col-span-5 space-y-2">
                                <div className="flex items-center gap-2 select-none">
                                  <FileText className="w-4 h-4 text-indigo-600" />
                                  <span className="font-bold text-xs text-slate-800 uppercase tracking-widest">
                                    Yeməyin Texniki Tapşırığı (TT)
                                  </span>
                                </div>
                                <div className="bg-white p-3.5 rounded-xl border border-slate-150 shadow-xs text-xs text-slate-600 leading-relaxed italic">
                                  {task.notes ? `"${task.notes}"` : "Xüsusi çəkiliş tələbləri mövcu deyil. Standart rəqəmsallaşdırma protokolu tətbiq olunur."}
                                </div>
                              </div>

                              <div className="lg:col-span-7 space-y-2">
                                <span className="font-bold text-xs text-slate-850 uppercase tracking-widest block select-none">
                                  📋 Alt tapşırıqlar və Müvafiq İcraçılar:
                                </span>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {/* Subtask 1 */}
                                  <div className="bg-white p-3 rounded-lg border border-slate-150 flex items-center justify-between gap-3 text-xs">
                                    <div className="space-y-0.5 min-w-0">
                                      <p className="font-extrabold text-slate-800">1. 9:16 Çəkiliş</p>
                                      <p className="text-[10px] text-slate-450 truncate">İcraçı: <span className="text-indigo-600 font-bold">{task.photoExecutor || task.executor}</span></p>
                                    </div>
                                    <span className={`text-[9px] font-bold font-mono px-1.5 py-0.5 rounded ${
                                      task.photoStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                                      task.photoStatus === 'in_progress' ? 'bg-amber-50 text-amber-700 animate-pulse' :
                                      'bg-slate-100 text-slate-500'
                                    }`}>
                                      {task.photoStatus === 'completed' ? 'Hazırdır' : task.photoStatus === 'in_progress' ? 'İcrada' : 'Gözlənilir'}
                                    </span>
                                  </div>

                                  {/* Subtask 2 */}
                                  <div className="bg-white p-3 rounded-lg border border-slate-150 flex items-center justify-between gap-3 text-xs">
                                    <div className="space-y-0.5 min-w-0">
                                      <p className="font-extrabold text-slate-800">2. Təbəqələr (Burst)</p>
                                      <p className="text-[10px] text-slate-450 truncate">İcraçı: <span className="text-indigo-600 font-bold">{task.burstExecutor || task.executor}</span></p>
                                    </div>
                                    <span className={`text-[9px] font-bold font-mono px-1.5 py-0.5 rounded ${
                                      task.burstStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                                      task.burstStatus === 'in_progress' ? 'bg-amber-50 text-amber-700 animate-pulse' :
                                      'bg-slate-100 text-slate-500'
                                    }`}>
                                      {task.burstStatus === 'completed' ? 'Hazırdır' : task.burstStatus === 'in_progress' ? 'İcrada' : 'Gözlənilir'}
                                    </span>
                                  </div>

                                  {/* Subtask 3 */}
                                  <div className="bg-white p-3 rounded-lg border border-slate-150 flex items-center justify-between gap-3 text-xs">
                                    <div className="space-y-0.5 min-w-0">
                                      <p className="font-extrabold text-slate-800">3. Sİ Video effekti</p>
                                      <p className="text-[10px] text-slate-450 truncate">İcraçı: <span className="text-indigo-600 font-bold">{task.aiVideoExecutor || task.executor}</span></p>
                                    </div>
                                    <span className={`text-[9px] font-bold font-mono px-1.5 py-0.5 rounded ${
                                      task.aiVideoStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                                      task.aiVideoStatus === 'in_progress' ? 'bg-amber-50 text-amber-700 animate-pulse' :
                                      'bg-slate-100 text-slate-500'
                                    }`}>
                                      {task.aiVideoStatus === 'completed' ? 'Hazırdır' : task.aiVideoStatus === 'in_progress' ? 'İcrada' : 'Gözlənilir'}
                                    </span>
                                  </div>

                                  {/* Subtask 4 */}
                                  <div className="bg-white p-3 rounded-lg border border-slate-150 flex items-center justify-between gap-3 text-xs">
                                    <div className="space-y-0.5 min-w-0">
                                      <p className="font-extrabold text-slate-800">4. QR Menyuya yüklə</p>
                                      <p className="text-[10px] text-slate-450 truncate">İcraçı: <span className="text-indigo-600 font-bold">{task.uploadExecutor || task.executor}</span></p>
                                    </div>
                                    <span className={`text-[9px] font-bold font-mono px-1.5 py-0.5 rounded ${
                                      task.uploadStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                                      task.uploadStatus === 'in_progress' ? 'bg-amber-50 text-amber-700 animate-pulse' :
                                      'bg-slate-100 text-slate-500'
                                    }`}>
                                      {task.uploadStatus === 'completed' ? 'Hazırdır' : task.uploadStatus === 'in_progress' ? 'İcrada' : 'Gözlənilir'}
                                    </span>
                                  </div>

                                </div>
                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-slate-50 px-5 py-3 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-450 font-bold font-mono select-none">
            <span>Göstərilən yemək sayısı: {filteredTasks.length} ədəd</span>
            <span className="text-indigo-550 hidden sm:inline">👉 Şəbəkə rəhbərinin təfərrüatlı kartını və texniki tapşırığı görmək üçün cədvəlin istənilən sətrinə klikləyin</span>
          </div>
        </div>
      ) : (
        /* GRID VIEW OF CARDS */
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredTasks.slice((currentPage - 1) * perPage, currentPage * perPage).map((task) => {
              const bColor = getStatusBadge(task.status);
              const isDetailOpen = activeTaskDetailId === task.id;

              return (
                <motion.div
                  key={task.id}
                  layoutId={task.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setSelectedTaskForModal(task)}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-indigo-150 cursor-pointer transition-colors shadow-sm overflow-hidden flex flex-col justify-between"
                  id={`task-card-${task.id}`}
                >
                  <div className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] text-indigo-650 font-bold uppercase font-mono tracking-wider select-none">
                          {task.restaurantName}
                        </p>
                        <h3 className="text-sm font-extrabold text-slate-900 mt-1">
                          {task.dishName}
                        </h3>
                      </div>

                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${bColor.bg} font-mono shrink-0 select-none`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${bColor.dot}`} />
                        <span>{bColor.label}</span>
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 py-3 px-4 bg-slate-50/70 border border-slate-100 rounded-xl">
                      <div className="space-y-0.5">
                        <span className="text-[9px] text-slate-400 font-mono font-bold uppercase block select-none">İcraçı</span>
                        <div className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-xs font-bold text-slate-700 truncate">{task.executor}</span>
                        </div>
                      </div>

                      <div className="space-y-0.5">
                        <span className="text-[9px] text-slate-400 font-mono font-bold uppercase block select-none">Tarix</span>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-xs font-semibold text-slate-600 font-mono">{task.createdAt}</span>
                        </div>
                      </div>
                    </div>

                    {/* Pipeline Stage Control & Interaction */}
                    <div className="space-y-2.5 pt-1.5">
                      <div className="flex items-center justify-between select-none">
                        <span className="text-[10px] text-slate-450 font-bold font-mono uppercase tracking-wider">İstehsal Mərhələləri:</span>
                        <span className="text-[9px] text-indigo-500 font-mono font-bold hover:text-indigo-700 cursor-pointer hidden md:inline">👉 Statusu tənzimləmək üçün klikləyin</span>
                      </div>

                      <div className="grid grid-cols-4 gap-2">
                        {/* Stage 1 */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubStatus(task.id, 'photoStatus', task.photoStatus);
                          }}
                          className={`flex flex-col items-center justify-center p-2 rounded-xl transition-all cursor-pointer border ${
                            task.photoStatus === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-250' :
                            task.photoStatus === 'in_progress' ? 'bg-amber-50 text-amber-600 border-amber-300 animate-pulse' :
                            'bg-slate-50 text-slate-400 border-slate-200'
                          }`}
                        >
                          <Camera className="w-3.5 h-3.5 font-bold" />
                          <span className="text-[7px] font-bold mt-1 text-center font-mono uppercase leading-tight">9:16 Çəkiliş</span>
                          <span className="text-[7px] text-slate-500 font-mono font-semibold truncate max-w-full text-center mt-0.5" title={task.photoExecutor || task.executor}>
                            👤 {task.photoExecutor || task.executor}
                          </span>
                        </button>

                        {/* Stage 2 */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubStatus(task.id, 'burstStatus', task.burstStatus);
                          }}
                          className={`flex flex-col items-center justify-center p-2 rounded-xl transition-all cursor-pointer border ${
                            task.burstStatus === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-250' :
                            task.burstStatus === 'in_progress' ? 'bg-amber-50 text-amber-600 border-amber-300 animate-pulse' :
                            'bg-slate-50 text-slate-400 border-slate-200'
                          }`}
                        >
                          <Layers className="w-3.5 h-3.5" />
                          <span className="text-[7px] font-bold mt-1 text-center font-mono uppercase leading-tight">Təbəqələr</span>
                          <span className="text-[7px] text-slate-500 font-mono font-semibold truncate max-w-full text-center mt-0.5" title={task.burstExecutor || task.executor}>
                            👤 {task.burstExecutor || task.executor}
                          </span>
                        </button>

                        {/* Stage 3 */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubStatus(task.id, 'aiVideoStatus', task.aiVideoStatus);
                          }}
                          className={`flex flex-col items-center justify-center p-2 rounded-xl transition-all cursor-pointer border ${
                            task.aiVideoStatus === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-250' :
                            task.aiVideoStatus === 'in_progress' ? 'bg-amber-50 text-amber-600 border-amber-300 animate-pulse' :
                            'bg-slate-50 text-slate-400 border-slate-200'
                          }`}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          <span className="text-[7px] font-bold mt-1 text-center font-mono uppercase leading-tight">Sİ Video</span>
                          <span className="text-[7px] text-slate-500 font-mono font-semibold truncate max-w-full text-center mt-0.5" title={task.aiVideoExecutor || task.executor}>
                            👤 {task.aiVideoExecutor || task.executor}
                          </span>
                        </button>

                        {/* Stage 4 */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubStatus(task.id, 'uploadStatus', task.uploadStatus);
                          }}
                          className={`flex flex-col items-center justify-center p-2 rounded-xl transition-all cursor-pointer border ${
                            task.uploadStatus === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-250' :
                            task.uploadStatus === 'in_progress' ? 'bg-amber-50 text-amber-600 border-amber-300 animate-pulse' :
                            'bg-slate-50 text-slate-400 border-slate-200'
                          }`}
                        >
                          <UploadCloud className="w-3.5 h-3.5" />
                          <span className="text-[7px] font-bold mt-1 text-center font-mono uppercase leading-tight">Menyuya QR</span>
                          <span className="text-[7px] text-slate-500 font-mono font-semibold truncate max-w-full text-center mt-0.5" title={task.uploadExecutor || task.executor}>
                            👤 {task.uploadExecutor || task.executor}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Expand and collapse note details */}
                    {task.notes && (
                      <div className="pt-2 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTaskDetailId(isDetailOpen ? null : task.id);
                          }}
                          className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-indigo-600 transition-colors cursor-pointer select-none"
                        >
                          <FileText className="w-3.5 h-3.5 text-slate-400" />
                          <span>{isDetailOpen ? 'Texniki brifi gizlə' : 'Texniki Tapşırığı (TT) göstər'}</span>
                        </button>

                        <AnimatePresence>
                          {isDetailOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0, marginTop: 0 }}
                              animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                              exit={{ opacity: 0, height: 0, marginTop: 0 }}
                              className="bg-indigo-50/40 p-3 rounded-xl border border-indigo-100/30 overflow-hidden text-slate-600 text-xs italic leading-relaxed"
                            >
                              "{task.notes}"
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>

                  {/* Card Actions Bottom Strip */}
                  <div className="bg-slate-50 px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[10px] text-slate-400 font-mono">
                      <span>KOD:</span>
                      <span className="font-bold">{task.id}</span>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm('Bu yeməyi istehsalat konveyerindən tamamilə silmək istədiyinizə əminsiniz?')) {
                          onDeleteTask(task.id);
                        }
                      }}
                      className="p-1.5 hover:bg-slate-200/60 text-slate-400 hover:text-rose-600 rounded-lg transition-all cursor-pointer border border-transparent"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* 6. Deep Details & Interactive Production Command Center Modal */}
      <AnimatePresence>
        {activeModalTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs"
            onClick={() => setSelectedTaskForModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              className="bg-white border border-slate-100 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative text-left"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Banner */}
              <div className="bg-gradient-to-r from-indigo-900 to-slate-900 text-white p-6 relative overflow-hidden">
                <div className="absolute right-0 bottom-0 opacity-10 translate-y-4 translate-x-4">
                  <ClipboardList className="w-64 h-64" />
                </div>
                
                {/* Close Button */}
                <button
                  onClick={() => setSelectedTaskForModal(null)}
                  className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full cursor-pointer transition-all border border-transparent"
                >
                  <X className="w-4 h-4" />
                </button>

                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2 select-none">
                    <span className="bg-indigo-500/30 border border-indigo-400/30 text-indigo-200 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full font-mono">
                      Tapşırıq haqqında məlumat
                    </span>
                    <span className="bg-slate-700/60 text-slate-300 text-[10px] font-medium font-mono px-2 py-1 rounded-md">
                      ID: {activeModalTask.id}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-xl md:text-2xl font-sans font-extrabold tracking-tight text-white">
                      {activeModalTask.dishName}
                    </h3>
                    <p className="text-slate-300 text-xs mt-1.5 flex items-center gap-1.5 font-medium">
                      <span>Restoran:</span>
                      <span className="text-white underline font-bold">{activeModalTask.restaurantName}</span>
                      <span className="text-slate-500">•</span>
                      <span>Əlavə edildi:</span>
                      <span className="font-mono text-slate-100">{activeModalTask.createdAt}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Main Content Sections */}
              <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
                
                {/* LEFT COL: Pipeline, Notes & Assignments */}
                <div className="md:col-span-7 space-y-6">
                  
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-1.5 select-none">
                      <TrendingUp className="w-4 h-4 text-indigo-600 font-bold" />
                      <span>İcra mərhələləri (Workflow)</span>
                    </h4>

                    {/* Overall Status display */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl select-none">
                      <span className="text-xs font-semibold text-slate-600">Ümumi rəqəmsallaşdırma statusu:</span>
                      {(() => {
                        const bColor = getStatusBadge(activeModalTask.status);
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${bColor.bg} font-mono`}>
                            <span className={`h-2 w-2 rounded-full ${bColor.dot}`} />
                            <span>{bColor.label}</span>
                          </span>
                        );
                      })()}
                    </div>

                    <div className="space-y-2.5 pt-1">
                      {/* Milestone 1 */}
                      <div className="flex items-start gap-3 p-3 bg-white border border-slate-150 rounded-xl hover:bg-slate-50/50 transition-all text-left">
                        <div className="mt-0.5 select-none">
                          {activeModalTask.photoStatus === 'completed' ? (
                            <div className="p-1 rounded-full bg-emerald-100 border border-emerald-250 text-emerald-700">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          ) : activeModalTask.photoStatus === 'in_progress' ? (
                            <div className="p-1 rounded-full bg-amber-100 border border-amber-250 text-amber-700 animate-pulse">
                              <Clock className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="p-1 rounded-full bg-slate-100 border border-slate-200 text-slate-400">
                              <Camera className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-800">1. Yeməyin 9:16 Çəkilişi</span>
                            <button
                              onClick={() => toggleSubStatus(activeModalTask.id, 'photoStatus', activeModalTask.photoStatus)}
                              className="text-[10px] text-indigo-600 font-bold hover:underline font-mono cursor-pointer"
                            >
                              Dəyişdir
                            </button>
                          </div>
                          <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                            Hekayə (story) formatına tam uyğun olan şaquli, yüksək keyfiyyətli kadrın hazırlanması.
                          </p>
                        </div>
                      </div>

                      {/* Milestone 2 */}
                      <div className="flex items-start gap-3 p-3 bg-white border border-slate-150 rounded-xl hover:bg-slate-50/50 transition-all text-left">
                        <div className="mt-0.5 select-none">
                          {activeModalTask.burstStatus === 'completed' ? (
                            <div className="p-1 rounded-full bg-emerald-100 border border-emerald-250 text-emerald-700">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          ) : activeModalTask.burstStatus === 'in_progress' ? (
                            <div className="p-1 rounded-full bg-amber-100 border border-amber-250 text-amber-700 animate-pulse">
                              <Clock className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="p-1 rounded-full bg-slate-100 border border-slate-200 text-slate-400">
                              <Layers className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-800">2. Təbəqələrin ayrılması (Burst)</span>
                            <button
                              onClick={() => toggleSubStatus(activeModalTask.id, 'burstStatus', activeModalTask.burstStatus)}
                              className="text-[10px] text-indigo-600 font-bold hover:underline font-mono cursor-pointer"
                            >
                              Dəyişdir
                            </button>
                          </div>
                          <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                            Yeməyin tərkib təbəqələrinin və inqrediyentlərinin şəkillərdən fərdi şəkildə kəsilməsi və ayrılması.
                          </p>
                        </div>
                      </div>

                      {/* Milestone 3 */}
                      <div className="flex items-start gap-3 p-3 bg-white border border-slate-150 rounded-xl hover:bg-slate-50/50 transition-all text-left">
                        <div className="mt-0.5 select-none">
                          {activeModalTask.aiVideoStatus === 'completed' ? (
                            <div className="p-1 rounded-full bg-emerald-100 border border-emerald-250 text-emerald-700">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          ) : activeModalTask.aiVideoStatus === 'in_progress' ? (
                            <div className="p-1 rounded-full bg-amber-100 border border-amber-250 text-amber-700 animate-pulse">
                              <Clock className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="p-1 rounded-full bg-slate-100 border border-slate-200 text-slate-400">
                              <Sparkles className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-800">3. Sİ Video effektinin generasiyası</span>
                            <button
                              onClick={() => toggleSubStatus(activeModalTask.id, 'aiVideoStatus', activeModalTask.aiVideoStatus)}
                              className="text-[10px] text-indigo-600 font-bold hover:underline font-mono cursor-pointer"
                            >
                              Dəyişdir
                            </button>
                          </div>
                          <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                            Süni intellekt (Luma/Runway) vasitəsilə yeməkdən buxar, maye və digər dinamik 3D effektlərin hazırlanması.
                          </p>
                        </div>
                      </div>

                      {/* Milestone 4 */}
                      <div className="flex items-start gap-3 p-3 bg-white border border-slate-150 rounded-xl hover:bg-slate-50/50 transition-all text-left">
                        <div className="mt-0.5 select-none">
                          {activeModalTask.uploadStatus === 'completed' ? (
                            <div className="p-1 rounded-full bg-emerald-100 border border-emerald-250 text-emerald-700">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          ) : activeModalTask.uploadStatus === 'in_progress' ? (
                            <div className="p-1 rounded-full bg-amber-100 border border-amber-250 text-amber-700 animate-pulse">
                              <Clock className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="p-1 rounded-full bg-slate-100 border border-slate-200 text-slate-400">
                              <UploadCloud className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-800">4. QR Menyuya yerləşdirilməsi</span>
                            <button
                              onClick={() => toggleSubStatus(activeModalTask.id, 'uploadStatus', activeModalTask.uploadStatus)}
                              className="text-[10px] text-indigo-600 font-bold hover:underline font-mono cursor-pointer"
                            >
                              Dəyişdir
                            </button>
                          </div>
                          <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                            Hazır video materialların rəqəmsal menya yüklənilməsi, örtük şəkli tənizmlənməsi və QR kod test fəaliyyəti.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Note / Technical specification Editor */}
                  <div className="space-y-2 pt-2 text-left">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest flex items-center gap-1.5 select-none">
                      <FileText className="w-3.5 h-3.5 text-slate-500" />
                      <span>Texniki Tapşırıq (TT) / Çəkiliş normaları</span>
                    </h4>
                    
                    <div className="space-y-2">
                      <textarea
                        rows={3}
                        className="w-full text-xs p-3.5 border border-slate-200 focus:border-indigo-500 bg-slate-50/30 rounded-xl outline-hidden text-slate-800"
                        value={activeModalTask.notes || ''}
                        onChange={async (e) => {
                          const updatedNotes = e.target.value;
                          await onUpdateTask(activeModalTask.id, { notes: updatedNotes });
                        }}
                        placeholder="Məsul şəxs üçün çəkiliş brifini buraya qeyd edin... Dəyişikliklər avtomatik yadda saxlanılır!"
                      />
                      <p className="text-[10px] text-indigo-600 font-bold font-mono select-none">
                        ✍️ Texniki tapşırıq dəyişdirildiyi an avtomatik olaraq tənzimlənir
                      </p>
                    </div>
                  </div>

                </div>

                {/* RIGHT COL: Detailed Stage Delegations / Sub-Tasks */}
                <div className="md:col-span-5 space-y-6 bg-slate-50/70 p-6 rounded-2xl border border-slate-200/60 self-start text-left">
                  
                  <div className="border-b border-slate-200 pb-3">
                    <span className="text-[9px] text-indigo-600 font-bold uppercase tracking-widest font-mono block select-none">Məsul şəxslərin bölgüsü</span>
                    <h4 className="text-base font-extrabold text-slate-950 mt-1">
                      Mərhələlər üzrə səlahiyyətlər
                    </h4>
                    <p className="text-[11px] text-slate-450 mt-1">
                      Öhdəlikləri ogrid-konveyerdəki fərqli və müstəqil mütəxəssislərə asanlıqla yönləndirin:
                    </p>
                  </div>

                  <div className="space-y-4">
                    
                    {/* Stage 1 Delegate */}
                    <div className="bg-white p-3 rounded-xl border border-slate-150 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <Camera className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                          <span>1. 9:16 Çəkiliş</span>
                        </span>
                        <span className={`text-[8px] font-bold font-mono px-1.5 py-0.5 rounded uppercase select-none ${
                          activeModalTask.photoStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                          activeModalTask.photoStatus === 'in_progress' ? 'bg-amber-50 text-amber-700 animate-pulse' :
                          'bg-slate-50 text-slate-500'
                        }`}>
                          {activeModalTask.photoStatus === 'completed' ? 'Hazırdır' : activeModalTask.photoStatus === 'in_progress' ? 'İcrada' : 'Gözlənilir'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={activeModalTask.photoExecutor || activeModalTask.executor}
                          onChange={async (e) => {
                            await onUpdateTask(activeModalTask.id, { photoExecutor: e.target.value });
                          }}
                          className="flex-1 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs outline-hidden text-slate-700 font-bold cursor-pointer"
                        >
                          {uniqueExecutors.map(exec => (
                            <option key={exec} value={exec}>{exec}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Əlavə..."
                          className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800"
                          onChange={async (e) => {
                            if (e.target.value.trim()) {
                              await onUpdateTask(activeModalTask.id, { photoExecutor: e.target.value });
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Stage 2 Delegate */}
                    <div className="bg-white p-3 rounded-xl border border-slate-150 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <Layers className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                          <span>2. Təbəqələr (Burst)</span>
                        </span>
                        <span className={`text-[8px] font-bold font-mono px-1.5 py-0.5 rounded uppercase select-none ${
                          activeModalTask.burstStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                          activeModalTask.burstStatus === 'in_progress' ? 'bg-amber-50 text-amber-700 animate-pulse' :
                          'bg-slate-50 text-slate-500'
                        }`}>
                          {activeModalTask.burstStatus === 'completed' ? 'Hazırdır' : activeModalTask.burstStatus === 'in_progress' ? 'İcrada' : 'Gözlənilir'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={activeModalTask.burstExecutor || activeModalTask.executor}
                          onChange={async (e) => {
                            await onUpdateTask(activeModalTask.id, { burstExecutor: e.target.value });
                          }}
                          className="flex-1 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs outline-hidden text-slate-700 font-bold cursor-pointer"
                        >
                          {uniqueExecutors.map(exec => (
                            <option key={exec} value={exec}>{exec}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Əlavə..."
                          className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800"
                          onChange={async (e) => {
                            if (e.target.value.trim()) {
                              await onUpdateTask(activeModalTask.id, { burstExecutor: e.target.value });
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Stage 3 Delegate */}
                    <div className="bg-white p-3 rounded-xl border border-slate-150 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                          <span>3. Sİ Video effekti</span>
                        </span>
                        <span className={`text-[8px] font-bold font-mono px-1.5 py-0.5 rounded uppercase select-none ${
                          activeModalTask.aiVideoStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                          activeModalTask.aiVideoStatus === 'in_progress' ? 'bg-amber-50 text-amber-700 animate-pulse' :
                          'bg-slate-50 text-slate-500'
                        }`}>
                          {activeModalTask.aiVideoStatus === 'completed' ? 'Hazırdır' : activeModalTask.aiVideoStatus === 'in_progress' ? 'İcrada' : 'Gözlənilir'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={activeModalTask.aiVideoExecutor || activeModalTask.executor}
                          onChange={async (e) => {
                            await onUpdateTask(activeModalTask.id, { aiVideoExecutor: e.target.value });
                          }}
                          className="flex-1 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs outline-hidden text-slate-700 font-bold cursor-pointer"
                        >
                          {uniqueExecutors.map(exec => (
                            <option key={exec} value={exec}>{exec}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Əlavə..."
                          className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800"
                          onChange={async (e) => {
                            if (e.target.value.trim()) {
                              await onUpdateTask(activeModalTask.id, { aiVideoExecutor: e.target.value });
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Stage 4 Delegate */}
                    <div className="bg-white p-3 rounded-xl border border-slate-150 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <UploadCloud className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                          <span>4. Menyuya QR</span>
                        </span>
                        <span className={`text-[8px] font-bold font-mono px-1.5 py-0.5 rounded uppercase select-none ${
                          activeModalTask.uploadStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                          activeModalTask.uploadStatus === 'in_progress' ? 'bg-amber-50 text-amber-700 animate-pulse' :
                          'bg-slate-50 text-slate-500'
                        }`}>
                          {activeModalTask.uploadStatus === 'completed' ? 'Hazırdır' : activeModalTask.uploadStatus === 'in_progress' ? 'İcrada' : 'Gözlənilir'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={activeModalTask.uploadExecutor || activeModalTask.executor}
                          onChange={async (e) => {
                            await onUpdateTask(activeModalTask.id, { uploadExecutor: e.target.value });
                          }}
                          className="flex-1 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs outline-hidden text-slate-700 font-bold cursor-pointer"
                        >
                          {uniqueExecutors.map(exec => (
                            <option key={exec} value={exec}>{exec}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Əlavə..."
                          className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800"
                          onChange={async (e) => {
                            if (e.target.value.trim()) {
                              await onUpdateTask(activeModalTask.id, { uploadExecutor: e.target.value });
                            }
                          }}
                        />
                      </div>
                    </div>

                  </div>

                  {/* Reassign / Change Chief Curator */}
                  <div className="space-y-2 pt-3 border-t border-slate-200 text-xs">
                    <span className="text-[10px] text-slate-450 font-mono font-bold uppercase block select-none">Yemək üzrə rəhbər kurator (Lead):</span>
                    
                    <div className="flex gap-2">
                      <select
                        value={activeModalTask.executor}
                        onChange={async (e) => {
                          const newExec = e.target.value;
                          if (newExec) {
                            await onUpdateTask(activeModalTask.id, { executor: newExec });
                          }
                        }}
                        className="flex-1 px-2.5 py-1.5 border border-slate-200 focus:border-indigo-500 bg-white rounded-lg text-xs outline-hidden text-slate-707 font-bold shrink-0 cursor-pointer"
                      >
                        {uniqueExecutors.map(exec => (
                          <option key={exec} value={exec}>{exec}</option>
                        ))}
                      </select>

                      <input 
                        type="text" 
                        placeholder="Yazın..." 
                        className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white text-slate-800 font-bold"
                        onChange={async (e) => {
                          const val = e.target.value;
                          if (val.trim()) {
                            await onUpdateTask(activeModalTask.id, { executor: val });
                          }
                        }}
                      />
                    </div>
                  </div>

                </div>

              </div>

              {/* Modal Bottom Control Footer bar */}
              <div className="bg-slate-50 px-6 py-4 border-t border-slate-150 rounded-b-3xl flex justify-between items-center select-none">
                <button
                  type="button"
                  onClick={async () => {
                    if (confirm('Bu yeməyi istehsalat konveyerindən tamamilə silmək istədiyinizə əminsiniz?')) {
                      await onDeleteTask(activeModalTask.id);
                      setSelectedTaskForModal(null);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 hover:bg-rose-55 hover:text-rose-700 border border-transparent hover:border-rose-200 rounded-xl text-xs font-bold transition-all cursor-pointer text-rose-600"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Tapşırığı sil</span>
                </button>

                <button
                  onClick={() => setSelectedTaskForModal(null)}
                  className="px-5 py-2 bg-slate-900 text-white font-bold text-xs rounded-xl hover:bg-slate-800 transition-all cursor-pointer shadow-xs border border-slate-900"
                >
                  Bağla
                </button>
              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pagination */}
      {filteredTasks.length > 0 && (
        <div className="flex items-center justify-between pt-4">
          <span className="text-xs text-slate-500">
            {filteredTasks.length} tapşırıq (göstərilir: {Math.min((currentPage) * perPage, filteredTasks.length)} / {filteredTasks.length})
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors"
            >
              ← Əvvəl
            </button>
            <span className="text-xs font-mono text-slate-600 px-2">
              Səh {currentPage}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={filteredTasks.length < perPage}
              className="px-3 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors"
            >
              Sonra →
            </button>
          </div>
        </div>
      )}

    </div>


  );
}
